import { useEffect, useState } from "react";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, ShieldAlert, LogOut } from "lucide-react";

const TIMEOUTS = [
  { value: 30, label: "30 minutes" }, { value: 60, label: "1 hour" },
  { value: 120, label: "2 hours" }, { value: 240, label: "4 hours" },
  { value: 480, label: "8 hours" },
];
const LOCK_DURATIONS = [
  { value: 0, label: "Manual unlock only" },
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 60, label: "1 hour" },
  { value: 1440, label: "24 hours" },
];

const Field = ({ label, hint, children }) => (
  <div>
    <label className="text-label block mb-1.5">{label}</label>
    {children}
    {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
  </div>
);

export default function Settings() {
  const [form, setForm] = useState({
    session_timeout_minutes: 60,
    max_login_attempts: 5,
    lock_duration_minutes: 15,
    password_expiry_days: 0,
    track_login_activity: true,
    track_lead_access: true,
    track_user_actions: true,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/system-settings").then(({ data }) => setForm((f) => ({ ...f, ...data })))
      .catch(() => {});
  }, []);

  const save = async () => {
    setLoading(true);
    try {
      await api.put("/system-settings", form);
      localStorage.setItem("crm_session_timeout", String(form.session_timeout_minutes));
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const forceAll = async () => {
    if (!window.confirm("Force logout ALL users (except yourself)? Their current sessions become invalid.")) return;
    try {
      await api.post("/users/force-logout-all");
      toast.success("All users have been signed out");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="p-6 sm:p-8 space-y-6 max-w-3xl" data-testid="settings-page">
      <header>
        <p className="text-label">System</p>
        <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold mt-1">Security Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Session, lockout and tracking controls.</p>
      </header>

      <section className="border border-border rounded-sm bg-background p-6 space-y-5">
        <Field label="Session Timeout" hint="Users auto-logout after this idle window.">
          <Select value={String(form.session_timeout_minutes)} onValueChange={(v) => set("session_timeout_minutes")(parseInt(v, 10))}>
            <SelectTrigger data-testid="session-timeout-select" className="w-[260px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TIMEOUTS.map((t) => <SelectItem key={t.value} value={String(t.value)}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Maximum Login Attempts" hint="Account locks after this many failed attempts.">
            <input
              type="number"
              min={3}
              max={20}
              value={form.max_login_attempts}
              onChange={(e) => set("max_login_attempts")(parseInt(e.target.value, 10) || 5)}
              data-testid="max-attempts-input"
              className="w-32 bg-transparent border border-border rounded-sm px-3 py-2 text-sm"
            />
          </Field>

          <Field label="Account Lock Duration">
            <Select value={String(form.lock_duration_minutes)} onValueChange={(v) => set("lock_duration_minutes")(parseInt(v, 10))}>
              <SelectTrigger data-testid="lock-duration-select" className="w-[260px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LOCK_DURATIONS.map((t) => <SelectItem key={t.value} value={String(t.value)}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Password Expiry (days)" hint="0 = never expires.">
            <input
              type="number"
              min={0}
              max={365}
              value={form.password_expiry_days}
              onChange={(e) => set("password_expiry_days")(parseInt(e.target.value, 10) || 0)}
              data-testid="password-expiry-input"
              className="w-32 bg-transparent border border-border rounded-sm px-3 py-2 text-sm"
            />
          </Field>
        </div>

        <div className="pt-4 border-t border-border space-y-3">
          <h3 className="text-sm font-semibold">Activity Tracking</h3>
          <ToggleRow label="Track Login Activity"
            description="Record every login & logout with IP, device, browser."
            checked={form.track_login_activity} onChange={set("track_login_activity")}
            testId="toggle-track-login" />
          <ToggleRow label="Track Lead Access"
            description="Record which user opened which lead."
            checked={form.track_lead_access} onChange={set("track_lead_access")}
            testId="toggle-track-access" />
          <ToggleRow label="Track User Actions"
            description="Record CRUD actions in the audit log."
            checked={form.track_user_actions} onChange={set("track_user_actions")}
            testId="toggle-track-actions" />
        </div>

        <div className="flex justify-end pt-3 border-t border-border">
          <button onClick={save} disabled={loading} data-testid="save-settings-btn"
            className="text-white text-sm font-medium rounded-sm py-2 px-4 hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
            style={{ backgroundColor: "var(--theme-color)" }}>
            <Save className="w-4 h-4" /> Save settings
          </button>
        </div>
      </section>

      {/* Danger zone */}
      <section className="border border-destructive/30 rounded-sm bg-background p-6">
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
          <ShieldAlert className="w-4 h-4 text-destructive" /> Danger Zone
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Force Logout All Users</p>
            <p className="text-xs text-muted-foreground">Invalidates every active session except yours.</p>
          </div>
          <button onClick={forceAll} data-testid="force-logout-all-btn"
            className="text-sm font-medium rounded-sm py-1.5 px-3 border border-destructive text-destructive hover:bg-destructive/10 flex items-center gap-1.5">
            <LogOut className="w-4 h-4" /> Force logout all
          </button>
        </div>
      </section>
    </div>
  );
}

function ToggleRow({ label, description, checked, onChange, testId }) {
  return (
    <div className="flex items-start justify-between py-1.5">
      <div className="pr-4">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}
